import React, { useState, useContext, useEffect, useMemo } from "react";
import Equipments from "./Equipments";
import { IoMdCheckmark } from "react-icons/io";
import DefaultFee from "./DefaultFee";
import { Grid } from "@mui/material";
import CurrencyInput from "./input-elements/CurrencyInput";
import { EquipmentContext } from "../useContext/context";
import * as Yup from "yup";
import { Stack } from "@mui/material";
import ToggleSwitch from "./input-elements/ToggleSwitch";
import { Form } from "react-bootstrap";
import pricingValidation from "../pricing-validation";
import ToggleSwitchContainer from "./input-elements/ToggleSwitchContainer";
import PercentageInput from "./input-elements/PercentageInput";
import { EMS_OTHER_DEFAULT_FEE } from "../../../../constants";
import config from "../../../../config";
import ACHPricing from "./ACHPricing";

const rates = ["3", "3.25", "3.5", "3.75", "4"];

const DualPricing = ({
    emsEquipments,
    setFormDataByKeyValue,
    errors,
    form_data,
    allEquipments,
    removeError,
    isEditable = true,
}) => {
    const [selectedRate, setSelectedRate] = useState(
        form_data.dual_pricing_rate || "",
    );
    const [dualDefaults, setDualDefaults] = useState({});

    const { setErrors, org_id, pricingDefaults } = useContext(EquipmentContext);

    const dual_pricing_validation =
        org_id == config.paymentcloud.PAYCHEX_ORG_ID
            ? pricingValidation.pricingValidation.paychex_dual_pricing
            : pricingValidation.pricingValidation.dual_pricing;

    // Update dual pricing defaults when pricing defaults load
    useEffect(() => {
        if (pricingDefaults && pricingDefaults.dual_pricing) {
            setDualDefaults(pricingDefaults.dual_pricing);
        }
    }, [pricingDefaults]);

    // Set default values when component mounts or dual defaults change
    useEffect(() => {
        const defaultValues = {
            dual_pricing_rate: dualDefaults.dual_pricing_rate?.default || dual_pricing_validation.dual_pricing_rate.default,
            dual_pricing_per_transaction: dualDefaults.dual_pricing_per_transaction?.default || dual_pricing_validation.dual_pricing_per_transaction.default,
            dual_pricing_monthly_fee: dualDefaults.dual_pricing_monthly_fee?.default || dual_pricing_validation.dual_pricing_monthly_fee.default,
            dual_pricing_pin_debit_rate: dualDefaults.dual_pricing_pin_debit_rate?.default || dual_pricing_validation.dual_pricing_pin_debit_rate.default,
            dual_pricing_pin_debit_per_transaction: dualDefaults.dual_pricing_pin_debit_per_transaction?.default || dual_pricing_validation.dual_pricing_pin_debit_per_transaction.default,            
            dual_pricing_pin_debit_network_fee: "no",
            add_ach: "no",
            ebt: "no",
            fnf_number: "",
            ebt_transactions: "",
        };
        // Only set values that aren't already set in form_data
        Object.entries(defaultValues).forEach(([key, value]) => {
            if (form_data[key] === undefined || form_data[key] === "") {
                setFormDataByKeyValue(key, value);
            }
        });
        setSelectedRate(dualDefaults.dual_pricing_rate || '3.5');
    }, [dualDefaults]); // Re-run when dual defaults change

    // Create validation schema based on current dual defaults
    const validationSchema = useMemo(() => {
        return Yup.object().shape({
            dual_pricing_rate: Yup.number()
                .transform((value, originalValue) =>
                    originalValue === "" ? 0 : value,
                )
                .required("Dual Pricing Rate is required")
                .min(
                    dualDefaults.dual_pricing_rate?.minimum || dual_pricing_validation.dual_pricing_rate.minimum,
                    `Please enter a fee of $${(dualDefaults.dual_pricing_rate?.minimum || dual_pricing_validation.dual_pricing_rate.minimum).toFixed(2)} or higher.`,
                ),

            dual_pricing_monthly_fee: Yup.number()
                .transform((value, originalValue) =>
                    originalValue === "" ? 0 : value,
                )
                .required("Monthly Fee is required")
                .min(
                    dualDefaults.dual_pricing_monthly_fee?.minimum || dual_pricing_validation.dual_pricing_monthly_fee.minimum,
                    `Please enter a fee of $${(dualDefaults.dual_pricing_monthly_fee?.minimum || dual_pricing_validation.dual_pricing_monthly_fee.minimum).toFixed(2)} or higher.`,
                ),

            dual_pricing_pin_debit_rate: Yup.number()
                .transform((value, originalValue) =>
                    originalValue === "" ? 0 : value,
                )
                .required("PIN-based Debit Rate is required")
                .min(
                    dualDefaults.dual_pricing_pin_debit_rate?.minimum || dual_pricing_validation.dual_pricing_pin_debit_rate.minimum,
                    `Please enter a rate of ${(dualDefaults.dual_pricing_pin_debit_rate?.minimum || dual_pricing_validation.dual_pricing_pin_debit_rate.minimum).toFixed(2)}% or higher.`,
                ),

            dual_pricing_pin_debit_per_transaction: Yup.number()
                .transform((value, originalValue) =>
                    originalValue === "" ? 0 : value,
                )
                .required("PIN-based Debit Per Transaction fee is required")
                .min(
                    dualDefaults.dual_pricing_pin_debit_per_transaction?.minimum || dual_pricing_validation.dual_pricing_pin_debit_per_transaction.minimum,
                    `Please enter a fee of $${(dualDefaults.dual_pricing_pin_debit_per_transaction?.minimum || dual_pricing_validation.dual_pricing_pin_debit_per_transaction.minimum).toFixed(2)} or higher.`,
                ),

            dual_pricing_pin_debit_network_fee: Yup.string().required(
                "Network Fee selection is required",
            ),
            
            dual_pricing_per_transaction: Yup.number()
            .transform((value, originalValue) =>
                originalValue === "" ? 0 : value,
            )
            .required("Per Transaction fee is required")
            .min(
                dualDefaults.dual_pricing_per_transaction?.minimum || dual_pricing_validation.dual_pricing_per_transaction.minimum,
                `Please enter a fee of $${(dualDefaults.dual_pricing_per_transaction?.minimum || dual_pricing_validation.dual_pricing_per_transaction.minimum).toFixed(2)} or higher.`,
            ),
            
            fnf_number: Yup.number()
                .transform((value, originalValue) =>
                    originalValue === "" ? 0 : value,
                )
                .when("ebt", {
                    is: "yes",
                    then: Yup.number().required("FNF Number is Required"),
                    otherwise: Yup.number(),
                }),
        });
    }, [dualDefaults]); // Only recreate when dualDefaults change

    const handleOnChange = (e) => {
        try {
            e?.persist();
        } catch (error) {}
        setFormDataByKeyValue(e.target.name, e.target.value);
        validationSchema
            .validateAt(e.target.name, { [e.target.name]: e.target.value })
            .then((e) => {
                // nothing here
            })
            .catch((err) => {
                let validation_errors = errors;
                validation_errors = {
                    ...validation_errors,
                    [e.target.name]: err.errors,
                };
                setErrors(validation_errors);
            });
    };

    return (
        <div className="dual-pricing-section mt-4r">
            <p className="sub-heading-lead">
                Select your preferred Dual Pricing rate
            </p>
            <Grid container spacing={{ xs: 3, sm: 3, md: 4 }}>
                <Grid item xs={12} sm={12} md={6}>
                    {org_id == config.paymentcloud.PAYCHEX_ORG_ID ? (
                        <>
                            <PercentageInput
                                label="Dual Pricing %"
                                type="number"
                                placeholder={dualDefaults.dual_pricing_rate?.default || "3.5"}
                                name={"dual_pricing_rate"}
                                onChange={(e) => {
                                    setSelectedRate(e.target.value);
                                    setFormDataByKeyValue(
                                        "dual_pricing_rate",
                                        e.target.value,
                                    );
                                }}  
                                value={form_data.dual_pricing_rate}
                                errors={errors}
                                softLimit={dualDefaults?.dual_pricing_rate?.soft_limit}
                            />
                        </>
                    ) : (
                        <div className="pricing-model-buttons">
                            {rates.map((rate) => (
                                <>
                                    <button
                                        disabled={!isEditable}
                                        type="button"
                                        key={rate}
                                        className={
                                            parseFloat(form_data.dual_pricing_rate) === parseFloat(rate) ? "active small" : "small"
                                        }
                                        onClick={() => {
                                            setSelectedRate(rate);
                                            setFormDataByKeyValue(
                                                "dual_pricing_rate",
                                                rate,
                                            );
                                        }}
                                    >
                                        {parseFloat(form_data.dual_pricing_rate) === parseFloat(rate) ? (
                                            <IoMdCheckmark />
                                        ) : (
                                            ""
                                        )}{" "}
                                        {rate}%
                                    </button>
                                </>
                            ))} 
                        </div>
                    )}
                    {org_id != config.paymentcloud.PAYCHEX_ORG_ID && (
                        <>
                            {dualDefaults?.dual_pricing_rate?.soft_limit && 
                            parseFloat(form_data.dual_pricing_rate) < parseFloat(dualDefaults?.dual_pricing_rate?.soft_limit) && 
                            !errors?.dual_pricing_rate && (
                                <span style={{ color: "#ff9800", fontSize: "10px", marginLeft: "8px"}}>
                                    Manager approval required
                                </span>
                            
                            )}
                      </>
                    )}

                    {errors?.dual_pricing_rate &&
                        Array.isArray(errors.dual_pricing_rate) &&
                        errors.dual_pricing_rate.map((error) => (
                            <span className="error-message" key={`dual-pricing-rate-${error}`}>{error}</span>
                        ))}
                </Grid>

                <Grid item xs={12} sm={12} md={3}>
                    <CurrencyInput
                        className="no-margin"
                        label="Txn Fee"
                        value={form_data.dual_pricing_per_transaction}
                        placeholder={dualDefaults.dual_pricing_per_transaction?.default || "0.15"}
                        name="dual_pricing_per_transaction"
                        type="number"
                        onChange={handleOnChange}
                        errors={errors}
                        softLimit={dualDefaults?.dual_pricing_per_transaction?.soft_limit}
                    />
                    {errors?.dual_pricing_per_transaction &&
                        Array.isArray(errors.dual_pricing_per_transaction) &&
                        errors.dual_pricing_per_transaction.map((error) => (
                            <span className="error-message" key={`per-transaction-${error}`}>{error}</span>
                        ))}
                </Grid>

                <Grid item xs={12} sm={12} md={3}>
                    <CurrencyInput
                        className="no-margin"
                        label="Monthly Fee"
                        placeholder={dualDefaults.dual_pricing_monthly_fee?.default || "49.00"}
                        value={form_data.dual_pricing_monthly_fee}
                        name="dual_pricing_monthly_fee"
                        type="number"
                        onChange={handleOnChange}
                        errors={errors}
                        softLimit={dualDefaults?.dual_pricing_monthly_fee?.soft_limit}
                    />
                    {errors?.dual_pricing_monthly_fee &&
                        Array.isArray(errors.dual_pricing_monthly_fee) &&
                        errors.dual_pricing_monthly_fee.map((error) => (
                            <span className="error-message" key={`monthly-fee-${error}`}>{error}</span>
                        ))}
                </Grid>
            </Grid>

            <div className="equipment-box mt-2r">
                <p className="box-label">PIN-based Debit</p>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    className="container-col-2 mb-1r mt-2r"
                >
                    <div className="w-60">
                        <ToggleSwitchContainer
                            handleChange={(_e, value) =>
                                setFormDataByKeyValue(
                                    "dual_pricing_pin_debit_network_fee",
                                    value,
                                )
                            }
                            value={form_data.dual_pricing_pin_debit_network_fee}
                            options={[
                                { key: "yes", value: "Yes" },
                                { key: "no", value: "No" },
                            ]}
                            name="dual_pricing_pin_debit_network_fee"
                            label="Network Fee"
                        />
                        {errors?.dual_pricing_pin_debit_network_fee &&
                            Array.isArray(
                                errors.dual_pricing_pin_debit_network_fee,
                            ) &&
                            errors.dual_pricing_pin_debit_network_fee.map(
                                (error) => (
                                    <span className="error-message" key={`pin-debit-network-fee-${error}`}>
                                        {error}
                                    </span>
                                ),
                            )}
                    </div>
                    <div className="w-100">
                        <PercentageInput
                            label="Debit Rate"
                            type="number"
                            placeholder={dualDefaults.dual_pricing_pin_debit_rate?.default || "0.5"}
                            name={"dual_pricing_pin_debit_rate"}
                            onChange={handleOnChange}
                            value={form_data.dual_pricing_pin_debit_rate}
                            errors={errors}
                            softLimit={dualDefaults?.dual_pricing_pin_debit_rate?.soft_limit}
                            displaySoftLimitErrorMsg={form_data.dual_pricing_pin_debit_network_fee}
                        />
                        {errors?.dual_pricing_pin_debit_rate &&
                            Array.isArray(errors.dual_pricing_pin_debit_rate) &&
                            errors.dual_pricing_pin_debit_rate.map((error) => (
                                <span className="error-message" key={`pin-debit-rate-${error}`}>{error}</span>
                            ))}
                    </div>
                    <div className="w-100">
                        <CurrencyInput
                            label="Per Txn"
                            type="number"
                            placeholder={dualDefaults.dual_pricing_pin_debit_per_transaction?.default || "0.15"}
                            name={"dual_pricing_pin_debit_per_transaction"}
                            onChange={handleOnChange}
                            value={
                                form_data.dual_pricing_pin_debit_per_transaction
                            }
                            errors={errors}
                            softLimit={dualDefaults?.dual_pricing_pin_debit_per_transaction?.soft_limit}
                            displaySoftLimitErrorMsg={form_data.dual_pricing_pin_debit_network_fee}
                        />
                        {errors?.dual_pricing_pin_debit_per_transaction &&
                            Array.isArray(
                                errors.dual_pricing_pin_debit_per_transaction,
                            ) &&
                            errors.dual_pricing_pin_debit_per_transaction.map(
                                (error) => (
                                    <span className="error-message" key={`pin-debit-per-transaction-${error}`}>
                                        {error}
                                    </span>
                                ),
                            )}
                    </div>
                </Stack>
            </div>

            <div className="equipment-box mt-2r">
                <p className="box-label">ACH</p>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    className="container-col-2 mb-1r mt-2r"
                >
                    <div className="w-100 d-flex">
                        <ToggleSwitchContainer
                            handleChange={(_e, value) =>
                                setFormDataByKeyValue("add_ach", value)
                            }
                            value={form_data.add_ach}
                            options={[
                                { key: "yes", value: "Yes" },
                                { key: "no", value: "No" },
                            ]}
                            name="add_ach"
                            label="Add ACH"
                        />
                    </div>
                </Stack>
            </div>

            {form_data.add_ach === "yes" && (
                <ACHPricing
                    emsEquipments={emsEquipments}
                    setFormDataByKeyValue={setFormDataByKeyValue}
                    errors={errors}
                    form_data={form_data}
                    allEquipments={allEquipments}
                    removeError={removeError}
                    isAddOn={true}
                />
            )}

            <div className="equipment-box mt-2r ebt-transaction">
                <p className="box-label">EBT Transactions</p>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    className="container-col-2 mb-1r mt-2r"
                >
                    <div className="ebt-switch">
                        <ToggleSwitchContainer
                            handleChange={(_e, value) =>
                                setFormDataByKeyValue("ebt", value)
                            }
                            value={form_data.ebt}
                            options={[
                                { key: "yes", value: "Yes" },
                                { key: "no", value: "No" },
                            ]}
                            name="ebt"
                            label="EBT"
                        />
                        {errors?.ebt &&
                            Array.isArray(errors.ebt) &&
                            errors.ebt.map((error) => (
                                <span className="error-message" key={`ebt-${error}`}>{error}</span>
                            ))}
                    </div>
                    {form_data.ebt == "yes" && (
                        <div className="d-flex w-100">
                            <div className="floating-form w-100">
                                <Form.Group size="lg" controlId="fnf_number">
                                    <Form.Control
                                        placeholder="FNS Number"
                                        type="text"
                                        name="fnf_number"
                                        value={form_data.fnf_number}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (/^\d*$/.test(value)) {
                                                handleOnChange(e);
                                            }
                                        }}
                                        className="input-control"
                                    />
                                    <Form.Label className="floating-label">
                                        FNF Number
                                    </Form.Label>
                                </Form.Group>
                                {errors?.fnf_number &&
                                    Array.isArray(errors.fnf_number) &&
                                    errors.fnf_number.map((error) => (
                                        <span className="error-message" key={`fnf-number-${error}`}>
                                            {error}
                                        </span>
                                    ))}
                            </div>
                            <div className="floating-form w-100">
                                <CurrencyInput
                                    label="EBT Transactions"
                                    endAdornment="/txn"
                                    placeholder={
                                        EMS_OTHER_DEFAULT_FEE.ebt_transactions
                                    }
                                    value={form_data.ebt_transactions}
                                    name={"ebt_transactions"}
                                    type="number"
                                    onChange={handleOnChange}
                                    errors={errors}
                                />
                                {errors?.ebt_transactions &&
                                    Array.isArray(errors.ebt_transactions) &&
                                    errors.ebt_transactions.map((error) => (
                                        <span className="error-message" key={`ebt-transactions-${error}`}>
                                            {error}
                                        </span>
                                    ))}
                            </div>
                        </div>
                    )}
                </Stack>
            </div>
            <DefaultFee
                form_data={form_data}
                setFormDataByKeyValue={setFormDataByKeyValue}
                errors={errors}
            />
            {selectedRate && isEditable && (
                <Equipments
                    emsEquipments={emsEquipments}
                    setFormDataByKeyValue={setFormDataByKeyValue}
                    errors={errors}
                    form_data={form_data}
                    allEquipments={allEquipments}
                    removeError={removeError}
                />
            )}
        </div>
    );
};

export default DualPricing;
